import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, ClipboardCheck, User, FileText, Calendar } from 'lucide-react';

const STATUS_COLORS = {
  PENDING: 'secondary', IN_PROGRESS: 'default', SUBMITTED: 'default',
  APPROVED: 'default', REJECTED: 'destructive', OVERDUE: 'destructive',
};

export default function FormAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [templates, setTemplates] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [participantSearch, setParticipantSearch] = useState('');
  const [newAssign, setNewAssign] = useState({ template_id: '', target_type: 'PARTICIPANT', target_id: '', due_date: '', priority: 'NORMAL' });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await api.get(`/forms/assignments?${params}`);
      setAssignments(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load assignments'); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.allSettled([
      api.get('/forms/templates?status=PUBLISHED&pageSize=100'),
      api.get('/participants?pageSize=100'),
    ]).then(([tR, pR]) => {
      if (tR.status === 'fulfilled') setTemplates(tR.value.data.items || []);
      if (pR.status === 'fulfilled') setParticipants(pR.value.data.items || []);
    });
  }, []);

  const handleCreate = async () => {
    if (!newAssign.template_id) { toast.error('Select a template'); return; }
    if (newAssign.target_type === 'PARTICIPANT' && !newAssign.target_id) { toast.error('Select a participant'); return; }
    try {
      await api.post('/forms/assignments', newAssign);
      toast.success('Form assigned successfully');
      setShowCreate(false);
      setNewAssign({ template_id: '', target_type: 'PARTICIPANT', target_id: '', due_date: '', priority: 'NORMAL' });
      setParticipantSearch('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const filteredParticipants = participants.filter(p => {
    if (!participantSearch) return true;
    const q = participantSearch.toLowerCase();
    return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || p.ndis_number?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="form-assignments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><ClipboardCheck className="w-6 h-6" />Assignments</h1>
          <p className="text-muted-foreground mt-1">{total} assignments total</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-assignment-btn"><Plus className="w-4 h-4 mr-2" />Assign Form</Button>
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'ALL' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="assignment-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
            <SelectItem value="SUBMITTED">Submitted</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {assignments.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No assignments found</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => (
            <Card key={a.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(`/forms/assignments/${a.id}/fill`)} data-testid={`assignment-row-${a.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{a.template_name || 'Form'}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {a.target_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />{a.target_name}
                        </span>
                      )}
                      {!a.target_name && a.target_type && <span>{a.target_type}</span>}
                      <span>· {a.priority}</span>
                      {a.due_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Due: {a.due_date}</span>}
                    </div>
                  </div>
                </div>
                <Badge variant={STATUS_COLORS[a.status] || 'secondary'}>{a.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Create Assignment Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign Form to Participant</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template *</Label>
              <Select value={newAssign.template_id} onValueChange={v => setNewAssign(p => ({ ...p, template_id: v }))}>
                <SelectTrigger data-testid="assign-template-select"><SelectValue placeholder="Select a form template" /></SelectTrigger>
                <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Participant *</Label>
              <Input placeholder="Search by name or NDIS number..." value={participantSearch} onChange={e => setParticipantSearch(e.target.value)} className="mb-2" data-testid="assign-participant-search" />
              <div className="max-h-36 overflow-y-auto border rounded-md">
                {filteredParticipants.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No participants found</p>
                ) : filteredParticipants.map(p => (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 border-b last:border-0 transition-colors ${newAssign.target_id === p.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}
                    onClick={() => setNewAssign(f => ({ ...f, target_id: p.id }))}
                    data-testid={`assign-participant-${p.id}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                      {(p.first_name?.[0] || '')}{(p.last_name?.[0] || '')}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{p.first_name} {p.last_name}</p>
                      <p className="text-xs text-muted-foreground">NDIS: {p.ndis_number || 'N/A'}</p>
                    </div>
                    {newAssign.target_id === p.id && <Badge variant="default" className="ml-auto text-xs">Selected</Badge>}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={newAssign.due_date} onChange={e => setNewAssign(p => ({ ...p, due_date: e.target.value }))} />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={newAssign.priority} onValueChange={v => setNewAssign(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="confirm-assign-btn">Assign Form</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
